"use client";

import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { useIsMobile } from "../hooks/use-mobile";
import { cn } from "../lib/utils";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "./alert-dialog";
import { Button, type buttonVariants } from "./button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "./dialog";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from "./drawer";
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "./sheet";

/**
 * Responsive modal: centered Dialog on desktop, bottom vaul Drawer on mobile
 * (matches the sidebar's 768px breakpoint via useIsMobile).
 *
 * Credenza-style API: the root decides the variant once, sub-components
 * resolve through context so callers compose a single component tree.
 *
 * SSR: useIsMobile is false on the server and first client render, so markup
 * starts as the (closed) Dialog variant and swaps before anything opens.
 *
 * Three desktop presentations, picked with `variant` on the root:
 *
 * - `dialog` (default) — a centered Dialog. Unchanged from before this prop
 *   existed, so existing callers render exactly as they did.
 * - `alert` — a Radix AlertDialog: `role="alertdialog"`, no dismiss on outside
 *   click or Escape, focus parked on the safe choice. For confirmations whose
 *   consequence cannot be undone. On mobile the drawer is non-dismissible for
 *   the same reason, so a stray swipe cannot answer the question.
 * - `sheet` — an edge-anchored Sheet, for a working surface (a diff, a log)
 *   rather than a question. `side` picks the edge; mobile still gets the
 *   bottom drawer, which is the mobile-correct form of the same idea.
 *
 * The `alert` variant expects a controlled `open`: its action button does not
 * self-close, because a destructive confirmation almost always needs to stay
 * up showing a pending state while the request is in flight.
 */

type ResponsiveDialogVariant = "dialog" | "alert" | "sheet";

const ResponsiveDialogContext = React.createContext<{
	isMobile: boolean;
	variant: ResponsiveDialogVariant;
	side: "top" | "right" | "bottom" | "left";
}>({
	isMobile: false,
	variant: "dialog",
	side: "right",
});

function useResponsiveDialog() {
	return React.useContext(ResponsiveDialogContext);
}

interface ResponsiveDialogRootProps {
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	modal?: boolean;
	variant?: ResponsiveDialogVariant;
	/** Which edge the desktop `sheet` variant is anchored to. */
	side?: "top" | "right" | "bottom" | "left";
	children?: React.ReactNode;
}

function ResponsiveDialog({
	children,
	variant = "dialog",
	side = "right",
	...props
}: ResponsiveDialogRootProps) {
	const isMobile = useIsMobile();
	const contextValue = React.useMemo(
		() => ({ isMobile, variant, side }),
		[isMobile, variant, side],
	);

	if (isMobile) {
		return (
			<ResponsiveDialogContext.Provider value={contextValue}>
				{/* A destructive question must not be answerable by a stray swipe
				    or a tap on the scrim. */}
				<Drawer
					data-slot="responsive-dialog"
					dismissible={variant !== "alert"}
					{...props}
				>
					{children}
				</Drawer>
			</ResponsiveDialogContext.Provider>
		);
	}

	const Root =
		variant === "alert" ? AlertDialog : variant === "sheet" ? Sheet : Dialog;

	return (
		<ResponsiveDialogContext.Provider value={contextValue}>
			<Root data-slot="responsive-dialog" {...props}>
				{children}
			</Root>
		</ResponsiveDialogContext.Provider>
	);
}

type ResponsiveDialogSlotProps = React.ComponentProps<"button"> & {
	asChild?: boolean;
};

function ResponsiveDialogTrigger(props: ResponsiveDialogSlotProps) {
	const { isMobile, variant } = useResponsiveDialog();
	const Trigger = isMobile
		? DrawerTrigger
		: variant === "alert"
			? AlertDialogTrigger
			: variant === "sheet"
				? SheetTrigger
				: DialogTrigger;
	return <Trigger data-slot="responsive-dialog-trigger" {...props} />;
}

function ResponsiveDialogClose(props: ResponsiveDialogSlotProps) {
	const { isMobile, variant } = useResponsiveDialog();
	const Close = isMobile
		? DrawerClose
		: variant === "alert"
			? AlertDialogCancel
			: variant === "sheet"
				? SheetClose
				: DialogClose;
	return <Close data-slot="responsive-dialog-close" {...props} />;
}

function ResponsiveDialogContent({
	className,
	children,
	showCloseButton,
	...props
}: React.ComponentProps<"div"> & { showCloseButton?: boolean }) {
	const { isMobile, variant, side } = useResponsiveDialog();

	if (isMobile) {
		return (
			<DrawerContent
				data-slot="responsive-dialog-content"
				className={className}
				{...props}
			>
				{children}
			</DrawerContent>
		);
	}

	if (variant === "alert") {
		return (
			<AlertDialogContent
				data-slot="responsive-dialog-content"
				className={className}
				{...props}
			>
				{children}
			</AlertDialogContent>
		);
	}

	if (variant === "sheet") {
		return (
			<SheetContent
				data-slot="responsive-dialog-content"
				className={className}
				side={side}
				showCloseButton={showCloseButton}
				{...props}
			>
				{children}
			</SheetContent>
		);
	}

	return (
		<DialogContent
			data-slot="responsive-dialog-content"
			className={className}
			showCloseButton={showCloseButton}
			{...props}
		>
			{children}
		</DialogContent>
	);
}

function ResponsiveDialogHeader({
	className,
	...props
}: React.ComponentProps<"div">) {
	const { isMobile, variant } = useResponsiveDialog();
	const Header = isMobile
		? DrawerHeader
		: variant === "alert"
			? AlertDialogHeader
			: variant === "sheet"
				? SheetHeader
				: DialogHeader;
	return (
		<Header
			data-slot="responsive-dialog-header"
			className={cn(isMobile && "text-left!", className)}
			{...props}
		/>
	);
}

function ResponsiveDialogTitle(props: React.ComponentProps<"h2">) {
	const { isMobile, variant } = useResponsiveDialog();
	const Title = isMobile
		? DrawerTitle
		: variant === "alert"
			? AlertDialogTitle
			: variant === "sheet"
				? SheetTitle
				: DialogTitle;
	return <Title data-slot="responsive-dialog-title" {...props} />;
}

function ResponsiveDialogDescription(props: React.ComponentProps<"p">) {
	const { isMobile, variant } = useResponsiveDialog();
	const Description = isMobile
		? DrawerDescription
		: variant === "alert"
			? AlertDialogDescription
			: variant === "sheet"
				? SheetDescription
				: DialogDescription;
	return <Description data-slot="responsive-dialog-description" {...props} />;
}

/**
 * Scrollable main area. Adds the drawer's side padding on mobile (drawer
 * children are edge-to-edge, dialog content is already padded).
 */
function ResponsiveDialogBody({
	className,
	...props
}: React.ComponentProps<"div">) {
	const { isMobile } = useResponsiveDialog();
	return (
		<div
			data-slot="responsive-dialog-body"
			className={cn("overflow-y-auto", isMobile && "px-4", className)}
			{...props}
		/>
	);
}

function ResponsiveDialogFooter({
	className,
	...props
}: React.ComponentProps<"div">) {
	const { isMobile, variant } = useResponsiveDialog();
	const Footer = isMobile
		? DrawerFooter
		: variant === "alert"
			? AlertDialogFooter
			: variant === "sheet"
				? SheetFooter
				: DialogFooter;
	return (
		<Footer
			data-slot="responsive-dialog-footer"
			className={className}
			{...props}
		/>
	);
}

type ConfirmButtonProps = React.ComponentProps<"button"> &
	Pick<VariantProps<typeof buttonVariants>, "variant">;

/**
 * The button that carries out the action. Pair with `variant="destructive"`
 * whenever the consequence cannot be undone.
 *
 * Deliberately does not self-close on mobile: the caller owns `open` so the
 * dialog can hold a pending state while the request is in flight, and the two
 * platforms behave the same way as long as the desktop call site keeps the
 * usual `e.preventDefault()`.
 */
function ResponsiveDialogAction({ variant, ...props }: ConfirmButtonProps) {
	const { isMobile, variant: kind } = useResponsiveDialog();

	if (!isMobile && kind === "alert") {
		return (
			<AlertDialogAction
				data-slot="responsive-dialog-action"
				variant={variant}
				{...props}
			/>
		);
	}

	return (
		<Button
			data-slot="responsive-dialog-action"
			type="button"
			variant={variant}
			{...props}
		/>
	);
}

/** The safe way out. Always the quiet button, never the loud one. */
function ResponsiveDialogCancel({
	variant = "outline",
	...props
}: ConfirmButtonProps) {
	const { isMobile, variant: kind } = useResponsiveDialog();

	if (!isMobile && kind === "alert") {
		return (
			<AlertDialogCancel
				data-slot="responsive-dialog-cancel"
				variant={variant}
				{...props}
			/>
		);
	}

	const Close = isMobile
		? DrawerClose
		: kind === "sheet"
			? SheetClose
			: DialogClose;
	return (
		<Close asChild>
			<Button
				data-slot="responsive-dialog-cancel"
				type="button"
				variant={variant}
				{...props}
			/>
		</Close>
	);
}

export {
	ResponsiveDialog,
	ResponsiveDialogAction,
	ResponsiveDialogBody,
	ResponsiveDialogCancel,
	ResponsiveDialogClose,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
	ResponsiveDialogTrigger,
};
